import { useState } from 'react';
import { Twitter, Linkedin, Facebook, Link2, Check } from 'lucide-react';

/**
 * Social share row: Twitter / LinkedIn / Facebook links plus a copy-to-clipboard
 * button. Each control is icon-only, so every one carries an `aria-label`
 * (previously only `title=`, which is not a reliable accessible name).
 */
const ShareButtons = ({ url, title }) => {
  const [copied, setCopied] = useState(false);

  const links = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard may be unavailable (e.g. insecure context); ignore.
    }
  };

  const iconCls =
    'p-2 hover:bg-secondary rounded-full transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

  return (
    <div className="flex items-center gap-2" data-testid="social-share">
      <span className="font-sans text-sm text-muted-foreground mr-2">Share:</span>
      <a href={links.twitter} target="_blank" rel="noopener noreferrer" className={iconCls} aria-label="Share on Twitter" data-testid="share-twitter">
        <Twitter size={18} className="text-muted-foreground hover:text-primary" />
      </a>
      <a href={links.linkedin} target="_blank" rel="noopener noreferrer" className={iconCls} aria-label="Share on LinkedIn" data-testid="share-linkedin">
        <Linkedin size={18} className="text-muted-foreground hover:text-primary" />
      </a>
      <a href={links.facebook} target="_blank" rel="noopener noreferrer" className={iconCls} aria-label="Share on Facebook" data-testid="share-facebook">
        <Facebook size={18} className="text-muted-foreground hover:text-primary" />
      </a>
      <button onClick={copy} className={iconCls} aria-label="Copy link" data-testid="share-copy">
        {copied ? (
          <Check size={18} className="text-green-600" />
        ) : (
          <Link2 size={18} className="text-muted-foreground hover:text-primary" />
        )}
      </button>
    </div>
  );
};

export default ShareButtons;
